import BaseService from 'ember-intl/services/intl';
import { eachDayOfInterval, addDays } from 'date-fns';
import startOfWeek from '@dubbletrack/time/utils/start-of-week';
import { service } from '@ember/service';
import normalizeSpaces from '@dubbletrack/toolbox/utils/normalize-spaces';
import intlPolyfill from '@dubbletrack/toolbox/utils/intl-polyfill';

const MONTHS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(
  (month) => new Date(2020, month, 1),
);
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]
  .map((day) => new Date(2020, 2, day))
  .sort((d1, d2) => d1.getDay() > d2.getDay());

export default class IntlService extends BaseService {
  @service clock;

  async loadAndSetLocale(locale) {
    await intlPolyfill(locale);
    this.setLocale(locale);
  }

  _format(dateOrDates, formatting /*, locale */) {
    const results = []
      .concat(dateOrDates)
      .map((d) => this.formatDate(d, formatting));
    if (results.length > 1) {
      return results;
    }

    return results[0];
  }

  formatDuration(value, options = { style: 'short' }) {
    try {
      return new Intl.DurationFormat(this.primaryLocale, options).format(value);
    } catch (e) {
      console.error("couldn't format duration", e);
      return '';
    }
  }

  formatOrdinal(number, options = { style: 'short' }) {
    const pr = new Intl.PluralRules(this?.primaryLocale || 'en-US', {
      type: 'ordinal',
    });
    const suffix = { one: 'st', two: 'nd', few: 'rd', other: 'th' };
    return `${number}${suffix[pr.select(number)]}`;
  }

  formatDate() {
    return normalizeSpaces(super.formatDate(...arguments));
  }

  formatDateToParts(value, options = {}) {
    const dateOptions = {
      weekday: 'long',
      dayPeriod: 'short',
      timeZoneName: 'longOffset',
      ...options,
    };
    const dateTimeFormat = new Intl.DateTimeFormat(
      this.primaryLocale,
      dateOptions,
    );
    const parts = dateTimeFormat.formatToParts(value);

    let partsMap = {};
    parts
      .filter((part) => part.type !== 'literal')
      .forEach((part) => {
        partsMap[part.type] = part.value;
      });
    const timeZoneName = partsMap.timeZoneName;
    if (timeZoneName) {
      const offsetMatch = timeZoneName.match(
        /(GMT|UTC)?(([+-])?(\d\d)\:(\d\d))?/,
      );
      if (offsetMatch) {
        if (offsetMatch[1] === 'UTC') {
          partsMap._utcOffset = 0;
        } else if (offsetMatch[1] === 'GMT') {
          partsMap._utcOffset = 0;

          if (offsetMatch[4] && offsetMatch[5]) {
            const sign = offsetMatch[3] == '-' ? -1 : 1;
            const hours = parseInt(offsetMatch[4], 10);
            const minutes = parseInt(offsetMatch[5], 10);
            partsMap._utcOffset = (hours * 60 + minutes) * sign;
          }
        }
      }
    }
    return partsMap;
  }

  // Used to provide flatpickr with locale data
  get generatedLocaleData() {
    const months = {
      shorthand: this._format(
        MONTHS,
        { month: 'short' },
        { locale: this.primaryLocale },
      ),
      longhand: this._format(
        MONTHS,
        { month: 'long' },
        { locale: this.primaryLocale },
      ),
    };

    const weekdays = {
      shorthand: this._format(
        WEEKDAYS,
        { weekday: 'short' },
        { locale: this.primaryLocale },
      ),
      longhand: this._format(
        WEEKDAYS,
        { weekday: 'long' },
        { locale: this.primaryLocale },
      ),
    };

    let localeData = {
      firstDayOfWeek: 0,
      months,
      weekdays,
      // ordinal: (nth) => { },
      // time_24hr: false
    };

    return localeData;
  }

  // Used to translate repeat day info to timezones. TODO: maybe this should all happen on the backend
  get dayOrder() {
    let start = startOfWeek(new Date(), {
      weekStartsOn: this.generatedLocaleData.firstDayOfWeek,
    });
    return eachDayOfInterval({
      start,
      end: addDays(start, 6),
    }).map((d) => {
      return {
        dayIndex: d.getDay(),
        key: this.formatDate(d, {
          weekday: 'long',
          locale: 'en-US',
        }).toLowerCase(),
        localizedKey: this.formatDate(d, {
          weekday: 'long',
          locale: this.primaryLocale,
        }),
        label: this.formatDate(d, {
          weekday: 'long',
          locale: this.primaryLocale,
        }),
        shortLabel: this.formatDate(d, {
          weekday: 'short',
          locale: this.primaryLocale,
        }),
        date: d,
        format: (format) => this.formatDate(d, format),
      };
    });
  }
}
